import { datadogRum } from '@datadog/browser-rum';
import { config } from '@iheartjane/config';
import { useQueryClient } from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';

import { useGetServerVersion } from '@jane/shared/data-access';

import type { RootLoaderType } from '../../../data-access';
import {
  updateLocalCartUuid,
  useDeleteCart,
  useGetCart,
} from '../../../data-access';
import { useTypedRouteLoaderData } from '../../../hooks';
import { useModalsDisplayedContext } from '../ModalsDisplayedProvider';
import { useProductTracking } from '../ProductTrackingProvider';
import { useSearchContext } from '../SearchProvider';
import { useSessionContext } from '../SessionProvider';

interface ResetKioskProps {
  shouldDeleteCart?: boolean;
  shouldNavigateToMenu?: boolean;
}

export const useResetKiosk = () => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { resetSearchState } = useSearchContext();
  const { setCommsBannerDisplayed } = useModalsDisplayedContext();
  const {
    inlineAds: { resetInlineAdIds },
    cartToppers: { resetCartTopperIds },
  } = useProductTracking();

  const { resetSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { data: server } = useGetServerVersion();

  /**
   * Adding searchParams to the query is a way to tell the browser
   * to run a hard refresh of the page. See the service worker in '/kiosk/src/main'
   * for landing page implementation.
   * https://stackoverflow.com/questions/2099201/javascript-hard-refresh-of-current-page
   */
  const clientReload = useCallback(
    (shouldNavigateToMenu = true) => {
      if (shouldNavigateToMenu) {
        // send error to datadog if the client is not running the most up to date version
        if (config.buildVersion !== server?.buildVersion) {
          datadogRum.addError('Client reload', {
            type: 'KIOSK_RESET',
            versionFrom: config.buildVersion,
            versionTo: server?.buildVersion,
          });
        }
        // complete the reload
        window.location.assign(`stores/${storeId}?v=${nanoid()}`);
      }
    },
    [storeId, server?.buildVersion]
  );

  const onDeleteCartSuccess = useCallback(() => {
    resetInlineAdIds();
    resetCartTopperIds();
    resetSearchState();
    setCommsBannerDisplayed(false);
    resetSession();
  }, [
    resetInlineAdIds,
    resetCartTopperIds,
    resetSearchState,
    setCommsBannerDisplayed,
    resetSession,
  ]);

  const { mutate: deleteCart, isLoading } = useDeleteCart({
    onSuccess: onDeleteCartSuccess,
  });
  const { data: cart } = useGetCart();

  const resetKiosk = useCallback(
    (
      { shouldDeleteCart, shouldNavigateToMenu }: ResetKioskProps = {
        shouldDeleteCart: true,
        shouldNavigateToMenu: true,
      }
    ) => {
      if (!cart?.uuid) {
        onDeleteCartSuccess();
      }
      if (cart?.uuid && shouldDeleteCart) {
        deleteCart({ uuid: cart.uuid });
      } else {
        updateLocalCartUuid(null);
        queryClient.invalidateQueries({ queryKey: ['cart'] });
        onDeleteCartSuccess();
      }
      janeMixpanel.reset();
      clientReload(shouldNavigateToMenu);
    },
    [cart?.uuid, deleteCart, onDeleteCartSuccess, queryClient, clientReload]
  );
  return {
    isResetting: isLoading,
    resetKiosk,
  };
};
